import { Inject, Injectable } from '@angular/core';
import 'leaflet-control-geocoder';
import type { Point } from 'geojson';
import type { Observable } from 'rxjs';
import { map, of } from 'rxjs';
import * as L from 'leaflet';
import { HttpClient } from '@angular/common/http';
import { LoggerService, ParamSerializer } from 'src/app/shared';
import type { Address } from 'src/app/shared/generic/address';
import type { StopAddress } from 'src/app/shared/stops/stop';
import type { GeocodingCallback, IGeocoder } from 'leaflet-control-geocoder/dist/geocoders';

@Injectable()
export class GeocoderService implements IGeocoder {

  public constructor(@Inject(HttpClient) private readonly httpClient: HttpClient,
    @Inject(ParamSerializer) private readonly paramSerializer: ParamSerializer,
    @Inject(LoggerService) private readonly logger: LoggerService) { }


  public actualGeocode(query: string): Observable<{ formatted_address: string;
    geometry: Point;
    bbox: { northEast: [number, number]; southWest: [number, number] }; }[]> {
    if (!query) {
      return of([]);
    }

    const reg = query.match(/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/);

    if (reg !== null) {
      const coords = reg[0].split(',');
      const lat = parseFloat(coords[0].trim());
      const lng = parseFloat(coords[1].trim());
      return of([
        {
          formatted_address: reg[0],
          bbox: { northEast: [lat + 0.000001, lng + 0.000001],
            southWest: [lat - 0.000001, lng - 0.000001] },
          geometry: {
            type: 'Point',
            coordinates: [
              lng,
              lat,
            ],
          },
        },
      ]);
    }


    const newQuery = encodeURIComponent(query);

    const url = `https://nominatim.angeltrax.com/search?format=json&q=${newQuery}&addressdetails=1&limit=10`;

    return this.httpClient.get<NominatimSearchResult[]>(url, { observe: 'body' }).pipe(map(res => res.map(item => ({
      formatted_address: item.display_name,
      bbox: { northEast: [parseFloat(item.boundingbox[1]), parseFloat(item.boundingbox[3])],
        southWest: [parseFloat(item.boundingbox[0]), parseFloat(item.boundingbox[2])] },
      geometry: {
        type: 'Point',
        coordinates: [
          parseFloat(item.lon),
          parseFloat(item.lat),
        ],
      },
    }))));
  }


  public geocode(query: string, cb: GeocodingCallback, context?: any): void {
    this.actualGeocode(query).subscribe(
      {
        next: x => cb(x.map(z => ({
          center: new L.LatLng(z.geometry.coordinates[1], z.geometry.coordinates[0]),
          bbox: new L.LatLngBounds(z.bbox.northEast, z.bbox.southWest),
          name: z.formatted_address,
        }))),
        error: err => this.logger.apiErrorsToDisplayErrors(err),
      },
    );
  }


  public suggest?(query: string, cb: GeocodingCallback, context?: any): void {
    this.actualGeocode(query).subscribe(
      {
        next: x => cb(x.map(z => ({
          center: new L.LatLng(z.geometry.coordinates[1], z.geometry.coordinates[0]),
          bbox: new L.LatLngBounds(z.bbox.northEast, z.bbox.southWest),
          name: z.formatted_address,
        }))),
        error: err => this.logger.apiErrorsToDisplayErrors(err),
      },
    );
  }

  public reverse?(location: L.LatLngLiteral): Observable<{ formatted_address: string }[]> {
    const url = `https://nominatim.angeltrax.com/reverse?format=json&lat=${location.lat}&lon=${location.lng}&zoom=18&addressdetails=1`;

    return this.httpClient.get<NominatimReverseGeocodeResult>(url, { observe: 'body' }).pipe(map(res => [
      {
        formatted_address: res.display_name,
      },
    ]));
  }

  public reverseGeocodeToStopAddress(lat: number, lng: number): Observable<StopAddress> {
    const url = `https://nominatim.angeltrax.com/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;

    return this.httpClient.get<NominatimReverseGeocodeResult>(url, { observe: 'body' }).pipe(
      map(res => ({
        road_number: res.address.house_number ?? null,
        road: res.address.road ?? null,
        city: res.address.city ?? null,
        state: res.address.state ?? null,
        zip: res.address.postcode ?? null,
      })),
    );
  }

  public reverseGeocodeToAddress(lat: number, lng: number): Observable<Address> {
    const url = `https://nominatim.angeltrax.com/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;

    return this.httpClient.get<NominatimReverseGeocodeResult>(url, { observe: 'body' }).pipe(
      map(res => {
        const houseNumber = res.address.house_number ?? '';
        const road = res.address.road ?? '';
        const addr_line1 = [houseNumber, road].filter(str => str !== '').join(' ') || null;

        return {
          addr_line1,
          addr_line2: null,
          city: res.address.city ?? null,
          state: res.address.state ?? null,
          zip: res.address.postcode ?? null,
        };
      }),
    );
  }

}

interface NominatimReverseGeocodeResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  boundingbox: string[];
}

interface NominatimSearchResult {
  place_id: number;
  licence: string;
  osm_type: string;
  osm_id: number;
  lat: string;
  lon: string;
  display_name: string;
  class: string;
  type: string;
  importance: number;
  icon?: string;
  address?: {
    house_number?: string;
    road?: string;
    suburb?: string;
    city?: string;
    county?: string;
    state?: string;
    postcode?: string;
    country?: string;
    country_code?: string;
  };
  boundingbox: string[];
}
